import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Bot, User, Star, Sun, Moon, Orbit, LayoutGrid, Compass, Calendar, Heart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_CATEGORIES = [
  {
    id: 'basics',
    name: 'Les bases',
    icon: Sparkles,
    color: 'bg-sky-100 border-sky-300 text-sky-700',
    questions: [
      "Qu'est-ce que l'astrologie ?",
      "Quelle est la différence entre signe solaire et ascendant ?",
      "Comment lire un thème natal ?",
    ]
  },
  {
    id: 'signs',
    name: 'Les signes',
    icon: Star,
    color: 'bg-amber-100 border-amber-300 text-amber-700',
    questions: [
      "Quelles sont les caractéristiques du Scorpion ?",
      "Quels signes sont compatibles avec le Lion ?",
      "Qu'est-ce qu'un signe de Feu ?",
    ]
  },
  {
    id: 'planets',
    name: 'Les planètes',
    icon: Orbit,
    color: 'bg-purple-100 border-purple-300 text-purple-700',
    questions: [
      "Que représente Vénus en astrologie ?",
      "Quel est le rôle de Saturne ?",
      "Qu'est-ce que Mercure rétrograde ?",
    ]
  },
  {
    id: 'houses',
    name: 'Les maisons',
    icon: LayoutGrid,
    color: 'bg-teal-100 border-teal-300 text-teal-700',
    questions: [
      "Que représente la Maison 7 ?",
      "Quelle maison concerne la carrière ?",
      "Comment interpréter une maison vide ?",
    ]
  },
  {
    id: 'transits',
    name: 'Transits',
    icon: Calendar,
    color: 'bg-yellow-100 border-yellow-300 text-yellow-700',
    questions: [
      "Qu'est-ce qu'un transit planétaire ?",
      "Quand aura lieu le prochain retour de Saturne ?",
      "Comment les transits affectent-ils mon thème ?",
    ]
  },
  {
    id: 'love',
    name: 'Compatibilité',
    icon: Heart,
    color: 'bg-rose-100 border-rose-300 text-rose-700',
    questions: [
      "Quels signes sont les plus compatibles ?",
      "L'ascendant compte-t-il pour la compatibilité ?",
      "Vénus et Mars en synastrie, ça veut dire quoi ?",
    ]
  },
];

const AstrozodiacChatPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('basics');
  const [birthData, setBirthData] = useState(null);
  const messagesEndRef = useRef(null);

  // Check for user birth data in localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('astrozodiac_birth_data');
    if (savedData) {
      setBirthData(JSON.parse(savedData));
    }
  }, []);

  useEffect(() => {
    const welcomeMessage = birthData 
      ? `Bienvenue ${birthData.name || ''} ! Je suis Stella, experte en astrologie. ✨\n\nJ'ai accès à tes données de naissance pour personnaliser mes réponses :\n• Date : ${birthData.birthDate}\n• Signe solaire : ${birthData.sunSign || 'À calculer'}\n\nPose-moi tes questions sur l'astrologie, les signes, les planètes ou les transits !`
      : `Bienvenue ! Je suis Stella, experte en astrologie. ✨\n\nCet espace est dédié à l'apprentissage de l'astrologie :\n• Comprendre les signes et leurs énergies\n• Décrypter les planètes et leur influence\n• Explorer les maisons et les aspects\n• Interpréter les transits actuels\n\n🌟 Pose tes questions pour percer les mystères des étoiles !\n\n💡 Pour une analyse personnalisée, calcule d'abord tes Big Three.`;
    
    setMessages([
      {
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      },
    ]);
  }, [birthData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: messageText,
          birth_data: birthData
        })
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: data.answer,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      
      const fallbackMessage = {
        role: 'assistant',
        content: "Je suis désolée, je rencontre un problème technique. Pour obtenir une analyse personnalisée de ton thème astral, je t'invite à calculer tes Big Three !",
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const currentCategory = QUESTION_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 flex flex-col">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-cyan-300 rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-sky-200 p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/astrozodiac')}
            className="rounded-full text-sky-500 hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
              <Bot className="h-5 w-5 text-gray-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Stella - Expert Astrologie</h1>
              <p className="text-xs text-sky-600">Pose tes questions sur les étoiles</p>
            </div>
          </div>
          {birthData && (
            <div className="hidden md:flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-sky-200">
              <Sun className="h-4 w-4 text-amber-400" />
              <span className="text-xs text-gray-700">{birthData.sunSign || '?'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Messages + Sidebar */}
      <div className="flex-1 flex overflow-hidden relative z-10">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user'
                        ? 'bg-cyan-600'
                        : 'bg-gradient-to-br from-sky-500 to-cyan-500'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 text-gray-900" />
                    ) : (
                      <Bot className="h-4 w-4 text-gray-900" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[85%] ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block p-4 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-cyan-600 text-gray-900'
                          : 'bg-white border border-sky-200 text-gray-700'
                      }`}
                    >
                      <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 px-2">
                      {message.timestamp.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-gray-900" />
                </div>
                <div className="bg-white/80 border border-sky-200 p-4 rounded-2xl">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    </div>
                    <span className="text-xs text-sky-500 ml-2">Stella consulte les étoiles...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Sidebar - Categories & Questions */}
        <div className="hidden lg:block w-80 border-l border-sky-200 bg-gray-50 overflow-y-auto">
          {/* Category Tabs */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-sky-200 p-3">
            <h3 className="text-sm font-semibold mb-3 text-gray-900">Catégories</h3>
            <div className="flex flex-wrap gap-1.5">
              {QUESTION_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-2.5 py-1.5 text-xs rounded-full border transition-all ${
                    activeCategory === category.id
                      ? category.color + ' font-semibold'
                      : 'bg-white border-sky-200 text-gray-600 hover:bg-sky-50'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Questions for Selected Category */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              {currentCategory && <currentCategory.icon className="h-4 w-4 text-sky-600" />}
              <span className="text-xs font-semibold text-gray-900">{currentCategory?.name}</span>
            </div>
            <div className="space-y-2">
              {currentCategory?.questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  disabled={isLoading}
                  className="w-full px-3 py-2.5 text-xs rounded-xl border transition-colors text-left leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed bg-white border-sky-100 text-gray-700 hover:bg-sky-50 hover:border-sky-300"
                >
                  {question}
                </button>
              ))}
            </div>
            
            {/* CTA */}
            <div className="mt-6 p-4 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-xl border border-sky-200">
              <p className="text-xs text-cyan-200 mb-3 font-medium">
                Envie d'explorer TON thème astral ?
              </p>
              <Button
                onClick={() => navigate('/astrozodiac/big-three')}
                size="sm"
                className="w-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400 text-gray-900 text-xs"
              >
                <Sun className="mr-2 h-3 w-3" />
                Découvrir mes Big Three
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Categories & Questions */}
      <div className="lg:hidden border-t border-sky-200 p-3 bg-white/80 backdrop-blur-md relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
            {QUESTION_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${
                  activeCategory === category.id
                    ? category.color + ' font-semibold'
                    : 'bg-white border-sky-200 text-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          {/* Questions */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {currentCategory?.questions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                disabled={isLoading}
                className="flex-shrink-0 px-3 py-2 text-xs bg-white hover:bg-sky-50 disabled:opacity-50 rounded-full border border-sky-200 transition-colors whitespace-nowrap text-gray-700"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="border-t-2 border-sky-200 p-4 bg-white/90 backdrop-blur-md shadow-lg relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-sky-500">✨ Question libre</span>
          </div>
          <div className="flex gap-3">
            <input
              data-testid="chat-input"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Pose ta question sur l'astrologie..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-sky-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
              disabled={isLoading}
            />
            <Button
              data-testid="send-button"
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="rounded-full w-12 h-12 p-0 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AstrozodiacChatPage;
